import {useState} from 'react';
import {NavLink} from 'react-router-dom';
import styled from 'styled-components';
import {useLogout} from '@/api/session';
import {useSession} from '@/hooks';
import {paths} from '@/router';
import {
  Divider,
  FlexCenter,
  FlexFullGrow,
  interceptProps,
  PaddingBox,
} from '@/styled-components';
import {FULL_SCREEN, LAYOUT_SIZE, LAYOUT_Z_INDEX, palette} from '@/styles';
import {Button} from '../Button';
import {CloseIcon, HamburgerMenuIcon} from '../Icons';
import {Typography, typographyStyle} from '../Typography';
import LogoImg from './assets/logo.svg';

interface IProps {
  disabled?: boolean;
  logoOnly?: boolean;
  defaultOpen?: boolean;
  onClose?: () => void;
}

export const MobileNavBar = ({
  disabled,
  logoOnly,
  defaultOpen,
  onClose,
}: IProps) => {
  const [open, setOpen] = useState<boolean>(defaultOpen ?? false);

  const toggle = () => {
    if (open) {
      onClose?.();
    }
    setOpen((prev) => !prev);
  };

  return (
    <Root disabled={disabled}>
      <NavLink to={paths.home}>
        <Logo>
          <img src={LogoImg} alt="마이프차" />
        </Logo>
      </NavLink>
      {!logoOnly && (
        <>
          <FlexFullGrow />
          <MoreButton onClick={toggle}>
            <img
              src={open ? CloseIcon : HamburgerMenuIcon}
              alt={open ? '메뉴 닫기' : '메뉴 열기'}
            />
          </MoreButton>
          {open && <Menu onClose={() => { setOpen(false); onClose?.(); }} />}
        </>
      )}
    </Root>
  );
};

const Menu = ({onClose}: {onClose(): void}) => {
  const {hasSession, user, isPaidUser, isSmartStoreActiveUser} = useSession();
  const {mutate} = useLogout();

  const logout = () => {
    mutate();
    onClose();
  };

  return (
    <MenuContainer>
      {hasSession && !!user ? (
        <>
          <div>
            <Typography variant="bold18">{user.nickname}</Typography>
            <PaddingBox top={4}>
              <Typography variant="medium12" color={palette.gray300}>
                {user.email}
              </Typography>
            </PaddingBox>
          </div>
          <NavLink to={{pathname: paths.mypage_account}} onClick={onClose}>
            <MenuItem>개인 정보 관리</MenuItem>
          </NavLink>
          <NavLink to={{pathname: paths.mypage_pay}} onClick={onClose}>
            <MenuItem>이용 이력</MenuItem>
          </NavLink>
          <MenuItem onClick={logout}>로그아웃</MenuItem>
        </>
      ) : (
        <FlexCenter>
          <NavLink to={paths.signin}>
            <Button variant="fill" color="primary" size="medium">
              로그인/회원가입
            </Button>
          </NavLink>
        </FlexCenter>
      )}
      <Divider />
      <MenuItem as={NavLink} to={paths.intro} onClick={onClose}>
        서비스 소개
      </MenuItem>
      <MenuItem as={NavLink} to={paths.pricing_root} onClick={onClose}>
        요금제
      </MenuItem>
      {isPaidUser && (
        <MenuItem as={NavLink} to={paths.map_root} onClick={onClose}>
          지도
        </MenuItem>
      )}
      {isSmartStoreActiveUser && (
        <SmartStoreTicket>
          <TicketDot />
          스마트상점 이용권 사용 중
        </SmartStoreTicket>
      )}
    </MenuContainer>
  );
};

const Root = styled('div').withConfig({
  shouldForwardProp: interceptProps(['disabled']),
})<{
  disabled?: boolean;
}>`
  ${({disabled}) => disabled && `opacity: 0.3;  pointer-events: none;`}
  background-color: ${palette.white};
  border-bottom: solid 1px ${palette.gray200};
  padding: 0 16px;
  height: ${LAYOUT_SIZE.NAV_HEIGHT}px;
  display: flex;
  position: relative;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
`;

const Logo = styled(FlexCenter)`
  width: 95px;
  height: 18px;
`;

const MoreButton = styled.button`
  cursor: pointer;
  img {
    width: 24px;
    height: 24px;
  }
`;

const MenuContainer = styled.div`
  position: absolute;
  height: calc(${FULL_SCREEN.vh} - ${LAYOUT_SIZE.NAV_HEIGHT}px);
  background-color: ${palette.white};
  z-index: ${LAYOUT_Z_INDEX.NAVBAR};
  top: ${LAYOUT_SIZE.NAV_HEIGHT}px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px 16px;
`;


const SmartStoreTicket = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1.5px dashed #34c78a;
  border-radius: 8px;
  background: #f0faf5;
  color: #1a7a4a;
  ${{...typographyStyle.medium14}}
  white-space: nowrap;
`;

const TicketDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #34c78a;
  flex-shrink: 0;
`;

const MenuItem = styled.div`
  cursor: pointer;
  ${{...typographyStyle.bold14}}
  &.active {
    color: ${palette.primary400};
    cursor: default;
  }
`;
